import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import { 
  Briefcase, GraduationCap, Award, Calendar, Sparkles, Download, 
  Linkedin, Twitter, Instagram, MapPin, Mail, Globe, Upload, Save,
  Users, Target, Mic, ExternalLink
} from 'lucide-react';
import { EditableText, EditableImage, AdminEditToggle } from '../components/admin/InlineEditor';
import { TiltCard } from '../components/home/TiltCard';
import { ScrollDepthReveal } from '../components/home/ScrollDepthReveal';

const AboutPage = () => {
  const { user, isAdmin } = useAuth();
  const [profileImage, setProfileImage] = useState('https://static.wixstatic.com/media/7a8ec4_6fcb4898c0814c3491e8b37b14dffde4~mv2.png');
  const [isEditingImage, setIsEditingImage] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Load profile image from backend
  useEffect(() => {
    fetchProfileImage();
  }, []);

  const fetchProfileImage = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pages/home/profile_image`);
      if (response.ok) {
        const data = await response.json();
        if (data.content?.url) {
          setProfileImage(data.content.url);
        }
      }
    } catch (error) {
      console.error('Error fetching profile image:', error);
    }
  };

  const saveProfileImage = async () => {
    if (!newImageUrl) return;
    try {
      const response = await fetch(`${API_URL}/api/pages/home/profile_image`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: { url: newImageUrl } })
      });
      if (response.ok) {
        setProfileImage(newImageUrl);
        setIsEditingImage(false);
        setNewImageUrl('');
      }
    } catch (error) {
      console.error('Error saving profile image:', error);
    }
  };

  const downloadCV = () => {
    // Generate CV content
    const cvContent = `
BIVAS MISHRA
Senior Business Analyst & Content Creator
================================================================================

CONTACT
-------
Email: hi@iambivas.in
LinkedIn: linkedin.com/in/bivas-mishra/
Twitter: @mishra_bivas
Instagram: @thebivasmishra

PROFESSIONAL SUMMARY
--------------------
A dynamic and results-driven Business Analyst with a passion for excellence. 
Currently serving esteemed clients in the EMEA region at Quoreka Group (Formerly 
Eka Software Solutions), managing critical responsibilities such as System Check, 
Product Functionality Check, Hands-on Management, and Query Management for 
organizations like Alvean Sugar, Providence, and BroadGrain.

Founder and Host of "Wickwise Pod with Bivas Mishra" - an inspiring podcast 
available on Spotify that engages thought leaders and experts, sharing valuable 
insights and uplifting stories.

PROFESSIONAL EXPERIENCE
-----------------------

Business Analyst | Quoreka Group (Eka Software Solutions)
2023 - Present
• Serving EMEA region clients including Alvean Sugar, Providence, BroadGrain
• Managing System Check, Product Functionality Check, Hands-on Management
• Query Management and client relationship building
• Data analysis and business process optimization

India Operations Executive | BRICS CCI Young Leaders
2021 - Present
• Foster Pan India collaboration and forge impactful MoUs with corporates
• Pioneered BRICS CCI Young Leader's Steering Committee Panel Network in China
• Enable strong international connections and cross-cultural learning

Co-Founder | Spasmodic Industry Academia Board (S.I.A.B.)
2020 - Present
• Dedicated to enhancing students' holistic growth and skill development
• Bridge between industrial exposure and academic knowledge
• Shaping well-rounded individuals for success

CEO | Spasmodic (under Lovely Professional University)
2019 - 2022
• Empowered students through diverse innovative events
• Provided invaluable corporate exposure and internship opportunities
• Strategic partnership with BRICS CCI Young Leaders Program
• Planned India's Largest Digital Student Entrepreneurship Summit 2021
• Achieved 1M+ viewership on social platforms

EDUCATION
---------

Integrated B.Com-MBA | Lovely Professional University
Mittal School of Business
Specialization: Marketing and Finance

SKILLS
------
• Business Analysis & Strategy
• CTRM (Commodity Trading & Risk Management)
• Digital Marketing
• Event Management
• Team Leadership & Coordination
• Client Relationship Management
• Data Analysis
• Public Speaking & Podcasting
• Cross-cultural Communication
• Strategic Planning

ACHIEVEMENTS
------------
• Founder & Host of "Wickwise Pod" podcast on Spotify
• Organized India's Largest Digital Student Entrepreneurship Summit 2021
• 1M+ social media viewership for entrepreneurship events
• Featured on YourStory
• 48+ months of hands-on experience in event management and digital marketing

SOCIAL LINKS
------------
• LinkedIn: https://www.linkedin.com/in/bivas-mishra/
• Twitter: https://x.com/mishra_bivas
• Instagram: https://www.instagram.com/thebivasmishra/
• Podcast: https://open.spotify.com/show/6l2wsDkyvwkHo96GG3DwUq

================================================================================
Generated from iambivas.in
`;

    const blob = new Blob([cvContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Bivas_Mishra_CV.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const experience = [
    {
      title: 'Senior Business Analyst',
      company: 'Quoreka Group (Eka Software Solutions)',
      period: 'Feb 2025 - Present',
      description: 'Leading strategic business analysis initiatives for EMEA region clients. Driving process optimization, stakeholder management, and delivering high-impact analytical solutions.',
      icon: Briefcase
    },
    {
      title: 'Business Analyst',
      company: 'Quoreka Group (Eka Software Solutions)',
      period: 'Aug 2023 - Feb 2025',
      description: 'Serving esteemed EMEA region clients including Alvean Sugar, Providence, and BroadGrain. Managing critical responsibilities such as System Check, Product Functionality Check, Hands-on Management, and Query Management.',
      icon: Briefcase
    },
    {
      title: 'Business Consultant',
      company: 'Quoreka Group (Eka Software Solutions)',
      period: 'Jan 2023 - Aug 2023',
      description: 'Provided strategic consulting services, analyzing business processes and recommending optimizations for improved operational efficiency.',
      icon: Briefcase
    },
    {
      title: 'Management Trainee',
      company: 'Quoreka Group (Eka Software Solutions)',
      period: 'Jan 2022 - Jan 2023',
      description: 'Comprehensive training across business operations, gaining hands-on experience in CTRM systems, client management, and business analysis fundamentals.',
      icon: Briefcase
    },
    {
      title: 'India Operations Executive',
      company: 'BRICS CCI Young Leaders',
      period: '2021 - Present',
      description: 'Fostering Pan India collaboration and forging impactful MoUs with reputed corporates. Pioneered the BRICS CCI Young Leader\'s Steering Committee Panel Network in China, enabling strong international connections.',
      icon: Globe
    },
    {
      title: 'Founder & Host',
      company: 'Wickwise Pod Podcast',
      period: '2021 - Present',
      description: 'An inspiring podcast on Spotify engaging thought leaders and experts, sharing valuable insights and uplifting stories to unlock individuals\' true potential and drive meaningful change.',
      icon: Mic
    },
    {
      title: 'Co-Founder',
      company: 'Spasmodic Industry Academia Board (S.I.A.B.)',
      period: '2020 - Present',
      description: 'Dedicated to enhancing students\' holistic growth and skill development. Bridging industrial exposure with academic knowledge to shape well-rounded individuals for success.',
      icon: Users
    },
    {
      title: 'CEO',
      company: 'Spasmodic (LPU)',
      period: '2019 - 2022',
      description: 'Empowered students through diverse innovative events, corporate exposure, and internship opportunities. Successfully planned India\'s Largest Digital Student Entrepreneurship Summit 2021 with 1M+ viewership.',
      icon: Target
    }
  ];

  const skills = [
    'Business Analysis', 'CTRM Systems', 'Strategic Planning', 'Digital Marketing',
    'Event Management', 'Team Leadership', 'Client Relations', 'Data Analysis',
    'Public Speaking', 'Podcasting', 'Cross-cultural Communication', 'Project Management',
    'Marketing Strategy', 'Finance', 'Stakeholder Management', 'Process Optimization'
  ];

  const achievements = [
    { number: '3+', label: 'Years at Quoreka' },
    { number: '55+', label: 'Months Experience' },
    { number: '100+', label: 'Events Organized' },
    { number: '50+', label: 'Corporate Partners' }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-16 lg:py-24 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/[0.05] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/[0.04] rounded-full blur-3xl" />
          
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Profile Image */}
              <ScrollDepthReveal direction="left" className="text-center lg:text-left">
                <TiltCard maxTilt={8} testId="about-profile-tilt" className="inline-block">
                  <div className="absolute -inset-3 bg-gradient-to-r from-white/20 via-white/10 to-white/20 rounded-3xl blur-lg opacity-70" />
                  <div className="relative">
                    <img
                      src={profileImage}
                      alt="Bivas Mishra"
                      className="w-72 h-72 lg:w-80 lg:h-80 object-cover rounded-3xl border-4 border-white/20 shadow-2xl"
                    />
                    {isAdmin && (
                      <button
                        onClick={() => setIsEditingImage(!isEditingImage)}
                        className="absolute bottom-4 right-4 p-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                      >
                        <Upload size={20} className="text-black" />
                      </button>
                    )}
                  </div>
                </TiltCard>
                
                {/* Admin Image Upload */}
                {isAdmin && isEditingImage && (
                  <div className="mt-4 p-4 bg-white rounded-xl shadow-lg border border-gray-200">
                    <p className="text-sm text-gray-600 mb-2">Enter new image URL:</p>
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={newImageUrl}
                        onChange={(e) => setNewImageUrl(e.target.value)}
                        placeholder="https://example.com/image.jpg"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={saveProfileImage}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                      >
                        <Save size={16} className="mr-1" /> Save
                      </button>
                    </div>
                  </div>
                )}
              </ScrollDepthReveal>
              
              {/* Bio */}
              <ScrollDepthReveal direction="right" delay={0.1}>
                <span className="inline-flex items-center px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full text-neutral-300 text-sm font-medium mb-6">
                  <Sparkles size={14} className="mr-2" />
                  Senior Business Analyst & Content Creator
                </span>
                
                <div className="mb-4">
                  <EditableText
                    pageId="about"
                    sectionId="hero"
                    fieldId="name"
                    defaultValue="Bivas Mishra"
                    as="h1"
                    className="text-4xl lg:text-5xl font-bold text-white"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  />
                </div>
                
                <div className="mb-6">
                  <EditableText
                    pageId="about"
                    sectionId="hero"
                    fieldId="quote"
                    defaultValue="Embrace the rhythm of life's constant beat"
                    as="p"
                    className="text-2xl text-neutral-300 font-medium italic"
                  />
                </div>
                
                <div className="mb-6">
                  <EditableText
                    pageId="about"
                    sectionId="hero"
                    fieldId="bio"
                    defaultValue="A dynamic and results-driven Business Analyst at Quoreka Group with a passion for excellence. Serving esteemed clients in the EMEA region while hosting 'Wickwise Pod' podcast on Spotify, engaging thought leaders and sharing valuable insights to drive meaningful change."
                    as="p"
                    className="text-neutral-400 text-lg leading-relaxed"
                  />
                </div>
                
                {/* Social Links */}
                <div className="flex gap-3 mb-6">
                  <a href="https://www.linkedin.com/in/bivas-mishra/" target="_blank" rel="noopener noreferrer" 
                    className="p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white hover:text-black hover:border-transparent transition-all">
                    <Linkedin size={20} className="text-neutral-300" />
                  </a>
                  <a href="https://x.com/mishra_bivas" target="_blank" rel="noopener noreferrer"
                    className="p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white hover:text-black hover:border-transparent transition-all">
                    <Twitter size={20} className="text-neutral-300" />
                  </a>
                  <a href="https://www.instagram.com/thebivasmishra/" target="_blank" rel="noopener noreferrer"
                    className="p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white hover:text-black hover:border-transparent transition-all">
                    <Instagram size={20} className="text-neutral-300" />
                  </a>
                </div>
                
                {/* Download CV Button */}
                <button
                  onClick={downloadCV}
                  className="inline-flex items-center px-6 py-3 bg-white text-black font-semibold rounded-full hover:bg-neutral-200 hover:shadow-lg hover:shadow-white/20 transition-all"
                >
                  <Download size={18} className="mr-2" />
                  Download CV
                </button>
              </ScrollDepthReveal>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-black border-y border-white/10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {achievements.map((item, index) => (
                <ScrollDepthReveal key={index} delay={index * 0.08} className="text-center">
                  <div className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-white via-neutral-300 to-neutral-500 bg-clip-text text-transparent mb-1">
                    {item.number}
                  </div>
                  <div className="text-neutral-400 text-sm">{item.label}</div>
                </ScrollDepthReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Experience Section */}
        <section className="py-16 lg:py-24 bg-[#0a0a0c]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollDepthReveal className="flex items-center justify-center mb-12">
              <div className="p-3 bg-white/10 rounded-xl mr-4 shadow-lg">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Professional Journey
              </h2>
            </ScrollDepthReveal>

            <div className="space-y-6">
              {experience.map((exp, index) => (
                <ScrollDepthReveal key={index} delay={Math.min(index * 0.06, 0.3)}>
                  <TiltCard maxTilt={5} hoverScale={1.01} testId={`about-exp-tilt-${index}`} className="group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-white/10 via-white/5 to-white/10 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative bg-white/[0.04] rounded-2xl p-6 border border-white/10 group-hover:border-white/30 transition-all shadow-lg shadow-black/30">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-white/10 rounded-xl flex-shrink-0">
                          <exp.icon size={24} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-white">{exp.title}</h3>
                            <span className="px-3 py-1 bg-white/10 text-neutral-200 text-sm font-medium rounded-full">
                              {exp.company}
                            </span>
                          </div>
                          <div className="flex items-center text-neutral-500 text-sm mb-3">
                            <Calendar size={14} className="mr-2" />
                            {exp.period}
                          </div>
                          <p className="text-neutral-300 leading-relaxed">{exp.description}</p>
                        </div>
                      </div>
                    </div>
                  </TiltCard>
                </ScrollDepthReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Education Section */}
        <section className="py-16 lg:py-24 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollDepthReveal className="flex items-center justify-center mb-12">
              <div className="p-3 bg-white/10 rounded-xl mr-4 shadow-lg">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Education
              </h2>
            </ScrollDepthReveal>

            <ScrollDepthReveal delay={0.1}>
              <TiltCard maxTilt={6} testId="about-education-tilt">
                <div className="bg-white/[0.04] rounded-3xl p-8 border border-white/10 shadow-xl text-center">
                  <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <GraduationCap size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Integrated B.Com-MBA</h3>
                  <p className="text-xl text-neutral-300 font-medium mb-2">Mittal School of Business</p>
                  <p className="text-neutral-400 mb-1">Lovely Professional University, Punjab</p>
                  <p className="text-neutral-500">Specialization: Marketing & Finance</p>
                </div>
              </TiltCard>
            </ScrollDepthReveal>
          </div>
        </section>

        {/* Skills Section */}
        <section className="py-16 lg:py-24 bg-[#0a0a0c]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollDepthReveal className="flex items-center justify-center mb-12">
              <div className="p-3 bg-white/10 rounded-xl mr-4 shadow-lg">
                <Award className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Skills & Expertise
              </h2>
            </ScrollDepthReveal>

            <div className="flex flex-wrap justify-center gap-3">
              {skills.map((skill, index) => (
                <span
                  key={index}
                  className="px-5 py-2.5 bg-white/5 border border-white/10 rounded-full text-neutral-300 font-medium hover:bg-white hover:text-black hover:border-transparent transition-all cursor-default shadow-sm"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Let's Connect & Collaborate
            </h2>
            <p className="text-neutral-700 text-lg mb-8 max-w-2xl mx-auto">
              I am enthusiastic about connecting with professionals and leaders across industries. 
              Let's explore collaborations, share insights, or discuss exciting opportunities!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="/contact" 
                className="px-8 py-3 bg-black text-white font-semibold rounded-full hover:shadow-lg hover:bg-neutral-800 transition-all"
              >
                <Mail size={18} className="inline mr-2" />
                Get in Touch
              </a>
              <a 
                href="https://open.spotify.com/show/6l2wsDkyvwkHo96GG3DwUq" 
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3 bg-black/5 text-black font-semibold rounded-full hover:bg-black/10 transition-all border border-black/20"
              >
                <Mic size={18} className="inline mr-2" />
                Listen to Podcast
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <AdminEditToggle />
    </div>
  );
};

export default AboutPage;
